import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

export type HistoryPeriodOption = "week" | "month" | "year";

interface PeriodOption {
  label: string;
  value: HistoryPeriodOption;
}

export function useHistoryPeriod() {
  const { t, tm } = useI18n();

  const selectedPeriod = ref<HistoryPeriodOption>("year");
  const isOpenedSelect = ref(false);
  const monthNames = computed(() => tm("format.monthsGenitive") as string[]);

  const periodOptions = computed<PeriodOption[]>(() => [
    {
      label: t("history.periods.week"),
      value: "week",
    },
    {
      label: t("history.periods.month"),
      value: "month",
    },
    {
      label: t("history.periods.yearOption"),
      value: "year",
    },
  ]);

  function formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  function formatDateForSelect(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${day}.${month}.${year}`;
  }

  function formatDateToHuman(dateString: string): string {
    const [, month, day] = dateString.split("-").map(Number);

    return `${day} ${monthNames.value[month - 1] ?? ""}`.trim();
  }

  function getPeriodStartEnd(): { start: string; end: string } {
    const today = new Date();
    const start = new Date(today);

    if (selectedPeriod.value === "week") {
      start.setDate(today.getDate() - 6);
    }

    if (selectedPeriod.value === "month") {
      start.setDate(today.getDate() - 29);
    }

    if (selectedPeriod.value === "year") {
      start.setFullYear(today.getFullYear() - 1);
    }

    return {
      start: formatDateForApi(start),
      end: formatDateForApi(today),
    };
  }

  const periodText = computed(() => {
    const today = new Date();

    if (selectedPeriod.value === "year") {
      return t("history.periods.yearSummary");
    }

    const start = new Date(today);

    if (selectedPeriod.value === "week") {
      start.setDate(today.getDate() - 6);
    }

    if (selectedPeriod.value === "month") {
      start.setDate(today.getDate() - 29);
    }

    return `${formatDateForSelect(start)} – ${formatDateForSelect(today)}`;
  });

  const isCustomPeriod = computed(() => selectedPeriod.value !== "year");

  function openSelect() {
    isOpenedSelect.value = !isOpenedSelect.value;
  }

  function choosePeriod(value: HistoryPeriodOption) {
    selectedPeriod.value = value;
    isOpenedSelect.value = false;
  }

  function resetPeriod() {
    selectedPeriod.value = "year";
    isOpenedSelect.value = false;
  }

  return {
    choosePeriod,
    formatDateToHuman,
    getPeriodStartEnd,
    isCustomPeriod,
    isOpenedSelect,
    monthNames,
    openSelect,
    periodOptions,
    periodText,
    resetPeriod,
    selectedPeriod,
  };
}
